import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { ArrowRight, Calculator, TrendingDown } from 'lucide-react';
import { formatPrice } from '../lib/utils';
import { trackSimulator } from '../lib/analytics';

export const SIMULATOR_TABS = [
  { id: 'menage', label: 'Ménage', priceBefore: 29.99, pricePonctuel: 35.00 },
  { id: 'jardinage', label: 'Jardinage', priceBefore: 49.90, pricePonctuel: 55.00 },
];

export const SIMULATOR_FREQUENCIES = [
  { value: 'hebdomadaire', label: 'Chaque semaine', multiplier: 4 },
  { value: 'bi-mensuel', label: 'Toutes les 2 semaines', multiplier: 2 },
  { value: 'mensuel', label: '1 fois par mois', multiplier: 1 },
  { value: 'ponctuel', label: 'Ponctuel', multiplier: 1 },
];

const MIN_HOURS_PONCTUEL = 2;
const MIN_HOURS_REGULAR = 1;
const MAX_HOURS = 8;
const TAX_CREDIT_RATE = 0.5;

/** Pure pricing rule: monthly cost, 50 % tax credit and out-of-pocket amount. */
export function computeSimulation(tabId, hours, frequency) {
  const tab = SIMULATOR_TABS.find((t) => t.id === tabId);
  const freq = SIMULATOR_FREQUENCIES.find((f) => f.value === frequency);
  if (!tab || !freq) return null;
  const isPonctuel = frequency === 'ponctuel';
  const tarif = isPonctuel ? tab.pricePonctuel : tab.priceBefore;
  const totalMonth = tarif * hours * freq.multiplier;
  const credit = totalMonth * TAX_CREDIT_RATE;
  return {
    totalMonth, credit, resteACharge: totalMonth - credit, pricePerHourAfter: tarif * TAX_CREDIT_RATE,
    totalHoursMonth: hours * freq.multiplier, priceBefore: tarif, regularPrice: tab.priceBefore, isPonctuel,
  };
}

/** Simulator state, business rules (minimum hours) and audience tracking. */
function useSimulator(preselectedTab) {
  const [activeTab, setActiveTab] = useState(preselectedTab);
  const [hours, setHours] = useState(3);
  const [frequency, setFrequency] = useState('hebdomadaire');
  const [avanceImmediate, setAvanceImmediate] = useState(true);

  const isPonctuel = frequency === 'ponctuel';
  const minHours = isPonctuel ? MIN_HOURS_PONCTUEL : MIN_HOURS_REGULAR;

  // One "simulator use" per service and per page display, only after a real interaction
  const trackedServices = useRef(new Set());
  const noteUse = (service = activeTab) => {
    if (trackedServices.current.has(service)) return;
    trackedServices.current.add(service);
    trackSimulator(service);
  };

  const onTabChange = (val) => { setActiveTab(val); noteUse(val); };
  const onAvanceChange = (val) => { setAvanceImmediate(val); noteUse(); };
  const onFrequencyChange = (val) => {
    noteUse();
    setFrequency(val);
    if (val === 'ponctuel' && hours < MIN_HOURS_PONCTUEL) setHours(MIN_HOURS_PONCTUEL);
  };
  const onHoursChange = (val) => {
    noteUse();
    setHours(Math.max(val[0], minHours));
  };

  const results = useMemo(() => computeSimulation(activeTab, hours, frequency), [activeTab, hours, frequency]);
  const frequencyLabel = SIMULATOR_FREQUENCIES.find((f) => f.value === frequency)?.label.toLowerCase();

  return { activeTab, hours, frequency, avanceImmediate, isPonctuel, minHours, results, frequencyLabel, onTabChange, onAvanceChange, onFrequencyChange, onHoursChange };
}

function HoursField({ hours, minHours, isPonctuel, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-medium">Nombre d'heures par intervention</label>
        <Badge variant="secondary" className="font-semibold" data-testid="simulator-hours-input">{hours}h</Badge>
      </div>
      <Slider value={[hours]} onValueChange={onChange} min={minHours} max={MAX_HOURS} step={0.5} className="w-full" data-testid="simulator-hours-slider" />
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>{minHours}h{isPonctuel ? ' (min.)' : ''}</span>
        <span>{MAX_HOURS}h</span>
      </div>
      {isPonctuel && <p className="text-xs text-muted-foreground mt-1 italic">Intervention ponctuelle : minimum {MIN_HOURS_PONCTUEL}h par intervention</p>}
    </div>
  );
}

function FrequencyField({ frequency, onChange }) {
  return (
    <div>
      <label className="text-sm font-medium mb-2 block">Fréquence</label>
      <Select value={frequency} onValueChange={onChange}>
        <SelectTrigger data-testid="simulator-frequency-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {SIMULATOR_FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function SimulationResults({ results, avanceImmediate }) {
  if (!results) return null;
  const totalLabel = results.isPonctuel ? `Coût total (${results.totalHoursMonth}h)` : `Coût total / mois (${results.totalHoursMonth}h)`;
  const outOfPocketLabel = avanceImmediate ? 'Vous payez (avance immédiate)' : 'Reste à charge après déclaration';
  return (
    <div className="space-y-3 pt-4 border-t border-border">
      {results.isPonctuel && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-[hsl(var(--accent))]/10 text-xs text-muted-foreground">
          <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))] text-[10px] px-1.5 py-0">Ponctuel</Badge>
          Tarif ponctuel : {formatPrice(results.priceBefore)}/h (tarif régulier : {formatPrice(results.regularPrice)}/h)
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{totalLabel}</span>
        <span className="font-semibold" data-testid="simulator-result-total">{formatPrice(results.totalMonth)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5 text-green-600" /> Crédit d'impôt 50%</span>
        <span className="font-semibold text-green-600" data-testid="simulator-result-credit">- {formatPrice(results.credit)}</span>
      </div>
      <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--primary))]/10">
        <span className="text-sm font-semibold">{outOfPocketLabel}</span>
        <span className="text-xl font-bold text-[hsl(var(--primary))]" data-testid="simulator-result-out-of-pocket">{formatPrice(results.resteACharge)}</span>
      </div>
      <p className="text-xs text-muted-foreground text-center">
        Soit seulement <strong>{formatPrice(results.pricePerHourAfter)}/h</strong> après avance immédiate
        {avanceImmediate && !results.isPonctuel && ' — vous ne payez que ce montant chaque mois'}
        {results.isPonctuel && ' — intervention sans engagement'}
      </p>
    </div>
  );
}

export default function CostSimulator({ preselectedTab = 'menage', className = '', ville = '' }) {
  const sim = useSimulator(preselectedTab);
  const quoteState = { service: sim.activeTab, heures: String(sim.hours), frequence: sim.frequency, ...(ville ? { ville } : {}), from: 'simulateur' };

  return (
    <Card className={`p-6 sm:p-8 bg-card border border-border shadow-sm ${className}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center">
          <Calculator className="h-5 w-5 text-[hsl(var(--primary))]" />
        </div>
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold text-lg">Simulateur de coût</h3>
          <p className="text-sm text-muted-foreground">Estimez votre reste à charge après crédit d'impôt</p>
        </div>
      </div>

      <Tabs value={sim.activeTab} onValueChange={sim.onTabChange}>
        <TabsList className="w-full mb-6">
          {SIMULATOR_TABS.map((tab) => <TabsTrigger key={tab.id} value={tab.id} className="flex-1">{tab.label}</TabsTrigger>)}
        </TabsList>

        <div className="space-y-6">
          <HoursField hours={sim.hours} minHours={sim.minHours} isPonctuel={sim.isPonctuel} onChange={sim.onHoursChange} />
          <FrequencyField frequency={sim.frequency} onChange={sim.onFrequencyChange} />

          <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--brand-mint))]/50">
            <div>
              <span className="text-sm font-medium">Avance immédiate activée</span>
              <p className="text-xs text-muted-foreground">Vous ne payez que le reste à charge</p>
            </div>
            <Switch checked={sim.avanceImmediate} onCheckedChange={sim.onAvanceChange} data-testid="simulator-advance-switch" />
          </div>

          <SimulationResults results={sim.results} avanceImmediate={sim.avanceImmediate} />

          <Link to="/devis" state={quoteState}>
            <Button className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="simulator-quote-button">
              Recevoir ce devis personnalisé
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <p className="text-[11px] text-muted-foreground text-center -mt-2">Vos choix ({sim.hours}h, {sim.frequencyLabel}) seront pré-remplis dans la demande.</p>
        </div>
      </Tabs>
    </Card>
  );
}
